import { useEffect } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import type { Timeline, ToDoList } from '@crehana/compass.dashlets';
import { useFeatureFlag } from '@crehana/feature-flags/react';
import { AnnualImpactBanner } from '@crehana/hana-ai/AnnualImpactBanner';
import { AnnualPotentialBanner } from '@crehana/hana-ai/AnnualPotentialBanner';
import { HanaBannerCategories } from '@crehana/hana-ai/HanaBannerCategories';
import { useUITheme } from '@crehana/layouts';
import { useMediaQuery } from '@crehana/react-hooks';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Container } from '@crehana/ui';
import { useRouter } from 'next/router';

import { AuthUserRoleEnum } from '@/generated/graphql/types';
import { useCentralizedOrganization } from '@/shared/hooks/use-centralized-organization';

import { Card, Heading } from './components';
import {
  DynamicGridWidgets,
  type TWidget,
} from './components/dynamic-grid-widgets';
import { LearningCard } from './components/learning-card';
import { SocialHubCard } from './components/social-hub-card';
import { HOME_USER_UNIFIED_WIDGET_SOURCE } from './constants';
import { useHomeTranslations } from './hooks/use-home-translations';
import useHomeData from './useHomeData';
import { getRedirectToTalentUrl } from './utils';
import { AttendanceWidget } from './widgets/attendance-widget';
import { EmptyFirstMissionWidgetComponent } from './widgets/empty-first-mission-widget';
import { EmptyTalentNewsWidgetComponent } from './widgets/empty-talent-news-widget';
import { LastActivityUserWidget } from './widgets/last-activity-user-widget';
import { LearningAvailableContentsWidgetComponent } from './widgets/learning-available-contents-widget';
import { MilestonesEventsWidgetComponent } from './widgets/milestones-events-widget';
import { MyAssignedCoursesWidget } from './widgets/my-assigned-courses-widget';
import { MyLearningPathsWidget } from './widgets/my-learning-paths-widget';
import { MyTeamTalentWidget } from './widgets/my-team-talent-widget';
import { OutOfOfficeWidgetContainer } from './widgets/out-of-office-widget/out-of-office-widget';
import { TimelineWidget } from './widgets/time-line-widget';
import { ToDoListWidget } from './widgets/to-do-list-widget';
import { TodoListAdminWidget } from './widgets/todo-list-admin-widget';

interface IHomeV2Props {
  slug: string;
  /**
   * @description This prop is used to determine if the dynamic positioning
   *              widgets are enabled.
   */
  isDynamicPositioningWidgetsEnabled: boolean;
}

const Home: React.FC<IHomeV2Props> = ({
  slug,
  isDynamicPositioningWidgetsEnabled,
}) => {
  const router = useRouter();
  const { logEvent } = useAmplitude();
  const { flagValue: isTodoListAdminEnabled, loading: loadingTodoListAdmin } =
    useFeatureFlag('WIDGET_TODO_LIST');
  const { matches: isLargerMobile } = useMediaQuery('600px');
  const { matches: isXs } = useMediaQuery({ only: 'xs' });
  const isMobile = isXs;
  const { isDark } = useUITheme();
  const { cards } = useHomeData({ slug });
  const {
    flagValue: isMilestonesEventsEnabled,
    loading: loadingMilestonesEvents,
  } = useFeatureFlag('WIDGET_MILESTONES_EVENTS');
  const { flagValue: isOutOfOfficeWidgetEnabled, loading: loadingFeatureFlag } =
    useFeatureFlag('WIDGET_OUT_OF_OFFICE');

  const { originalId } = useCentralizedOrganization(slug);
  const { t } = useHomeTranslations();

  const onChatCreation = (chatId: string) => {
    logEvent({
      event: 'Main Banner Crehana AI Clicked',
      data: {
        ...getSourceEventParams(),
      },
    });
    router.push(`/org/${slug}/ai/user/chat/${chatId}`);
  };

  useEffect(() => {
    logEvent({
      event: 'Render Panel Home Cross V2',
      data: {
        ...getSourceEventParams(),
        Organization: slug,
        Success: true,
      },
    });

    // NOTE: This is a new event to track the render of the HomeUser Unified.
    //       The last event should be removed after the new event is stable.
    logEvent({
      event: 'Render HomeUser Unified',
      data: getSourceEventParams(),
    });
  }, [logEvent, slug]);

  // build the talent url
  const talentUrl = getRedirectToTalentUrl({
    centralizedId: originalId?.toString() || '',
    userRole: AuthUserRoleEnum.AuthRoleUser,
  });

  const renderContent = () => {
    // new widgets layout
    if (isDynamicPositioningWidgetsEnabled) {
      const widgets: TWidget<
        | typeof ToDoList
        | typeof Timeline
        | typeof Card
        | typeof SocialHubCard
        | typeof LearningCard
        | typeof MyAssignedCoursesWidget
        | typeof MyLearningPathsWidget
        | typeof MyTeamTalentWidget
        | typeof LastActivityUserWidget
        | typeof HanaBannerCategories
        | typeof TodoListAdminWidget
        | typeof MilestonesEventsWidgetComponent
        | typeof OutOfOfficeWidgetContainer
        | typeof EmptyFirstMissionWidgetComponent
        | typeof EmptyTalentNewsWidgetComponent
        | typeof LearningAvailableContentsWidgetComponent
        | typeof AttendanceWidget
      >[] = [
        {
          id: 'talent-tasks-to-complete',
          component: ToDoListWidget,
          props: {
            isProduction: process.env.APP_ENV === 'production',
            talentUrl: talentUrl,
          },
        } satisfies TWidget<typeof ToDoListWidget>,
        {
          id: 'talent-todo-list-v2',
          component: TodoListAdminWidget,
          props: {},
        } satisfies TWidget<typeof TodoListAdminWidget>,
        {
          id: 'talent-my-team',
          component: MyTeamTalentWidget,
          props: {
            organizationSlug: slug,
          },
        } satisfies TWidget<typeof MyTeamTalentWidget>,
        {
          id: 'out-of-office',
          component: OutOfOfficeWidgetContainer,
          props: {},
        } satisfies TWidget<typeof OutOfOfficeWidgetContainer>,
        {
          id: 'hana-end-user',
          component: HanaBannerCategories,
          props: {
            onChatCreation,
            targetSegment: 'end-user',
          },
          className: 'tw-max-h-fit',
        } satisfies TWidget<typeof HanaBannerCategories>,
        {
          id: 'talent-active-process',
          component: TimelineWidget,
          props: {},
        } satisfies TWidget<typeof TimelineWidget>,
        {
          id: 'learning-last-activity',
          component: LastActivityUserWidget,
          props: {
            amplitudeSource: HOME_USER_UNIFIED_WIDGET_SOURCE,
          },
        } satisfies TWidget<typeof LastActivityUserWidget>,
        {
          id: 'learning-my-assigned-courses',
          component: MyAssignedCoursesWidget,
          props: {
            organizationSlug: slug,
            amplitudeSource: HOME_USER_UNIFIED_WIDGET_SOURCE,
          },
        } satisfies TWidget<typeof MyAssignedCoursesWidget>,
        {
          id: 'learning-my-assigned-paths',
          component: MyLearningPathsWidget,
          props: {
            organizationSlug: slug,
            amplitudeSource: HOME_USER_UNIFIED_WIDGET_SOURCE,
          },
        } satisfies TWidget<typeof MyLearningPathsWidget>,
        {
          id: 'milestones-events',
          component: MilestonesEventsWidgetComponent,
          props: {},
        } satisfies TWidget<typeof MilestonesEventsWidgetComponent>,
        {
          id: 'empty-first-mission',
          component: EmptyFirstMissionWidgetComponent,
          props: {
            organizationSlug: slug,
          },
        } satisfies TWidget<typeof EmptyFirstMissionWidgetComponent>,
        {
          id: 'empty-talent-news',
          component: EmptyTalentNewsWidgetComponent,
          props: {
            organizationSlug: slug,
          },
        } satisfies TWidget<typeof EmptyTalentNewsWidgetComponent>,
        {
          id: 'learning-available-contents',
          component: LearningAvailableContentsWidgetComponent,
          props: {
            organizationSlug: slug,
          },
        } satisfies TWidget<typeof LearningAvailableContentsWidgetComponent>,
        {
          id: 'time-and-attendance',
          component: AttendanceWidget,
          props: {
            mode: 'widget',
          },
        } satisfies TWidget<typeof AttendanceWidget>,
      ];

      // TODO: This logic should be moved to backend.
      if (!originalId) {
        for (const card of cards) {
          widgets.push({
            id: 'learning-links',
            component: Card,
            props: { card, isDark },
          } satisfies TWidget<typeof Card>);
        }
      } else {
        widgets.push({
          id: 'talent-news',
          component: SocialHubCard,
          props: { centralizedOrganizationId: originalId },
        } satisfies TWidget<typeof SocialHubCard>);
        widgets.push({
          id: 'learning-links',
          component: LearningCard,
          props: {},
        } satisfies TWidget<typeof LearningCard>);
      }

      return <DynamicGridWidgets widgets={widgets} />;
    }

    // old widgets layout
    return (
      <>
        <div
          data-testid="home-widgets-grid"
          className="tw-grid tw-grid-cols-12 tw-gap-1.6 tw-pb-1.6"
        >
          {originalId && (
            <div className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6">
              <HanaBannerCategories
                onChatCreation={onChatCreation}
                targetSegment="end-user"
              />
            </div>
          )}

          {isTodoListAdminEnabled && !loadingTodoListAdmin && (
            <div className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6">
              <TodoListAdminWidget />
            </div>
          )}

          <div className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6">
            <ToDoListWidget
              talentUrl={talentUrl}
              isProduction={process.env.APP_ENV === 'production'}
            />
          </div>
          <div className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6">
            <TimelineWidget />
          </div>
        </div>

        <div className="tw-grid tw-grid-cols-12 tw-gap-1.6">
          {!originalId ? (
            cards.map(card => (
              <div
                key={card.title}
                className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6"
              >
                <Card card={card} isDark={isDark} />
              </div>
            ))
          ) : (
            <>
              <div className="tw-col-span-12 lg:tw-col-span-6">
                <SocialHubCard centralizedOrganizationId={originalId} />
              </div>

              <div className="tw-col-span-12 lg:tw-col-span-6">
                <LearningCard />
              </div>
            </>
          )}
          {isMilestonesEventsEnabled && !loadingMilestonesEvents && (
            <div className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6">
              <MilestonesEventsWidgetComponent />
            </div>
          )}

          {isOutOfOfficeWidgetEnabled && !loadingFeatureFlag && (
            <div className="tw-col-span-12 lg:tw-col-span-6">
              <OutOfOfficeWidgetContainer />
            </div>
          )}

          {/* New widgets */}
          <div className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6">
            <EmptyFirstMissionWidgetComponent organizationSlug={slug} />
          </div>

          <div className="tw-col-span-12 sm:tw-col-span-12 lg:tw-col-span-6">
            <EmptyTalentNewsWidgetComponent organizationSlug={slug} />
          </div>

          <div className="tw-col-span-12 lg:tw-col-span-6">
            <LearningAvailableContentsWidgetComponent organizationSlug={slug} />
          </div>
        </div>
      </>
    );
  };

  return (
    <main
      className={!isLargerMobile && !isDark ? 'tw-bg-neutral-light-100' : ''}
    >
      <AnnualImpactBanner
        onChatCreation={chatId => {
          router.push(`/org/${slug}/ai/user/chat/${chatId}/`);
        }}
        title={
          isMobile
            ? t('ANNUAL_IMPACT_BANNER_TITLE_MOBILE')
            : t('ANNUAL_IMPACT_BANNER_TITLE')
        }
        buttonLabel={t('LABEL_ANNUAL_IMPACT_BANNER_BUTTON')}
        hideButton={isMobile}
      />

      <AnnualPotentialBanner
        onChatCreation={chatId => {
          router.push(`/org/${slug}/ai/user/chat/${chatId}/`);
        }}
        title={
          isMobile
            ? t('ANNUAL_POTENTIAL_BANNER_TITLE_MOBILE')
            : t('ANNUAL_POTENTIAL_BANNER_TITLE')
        }
        buttonLabel={t('LABEL_ANNUAL_POTENTIAL_BANNER_BUTTON')}
        hideButton={isMobile}
      />

      <Container type="dashboard">
        <div className="tw-pt-4 tw-pb-2.4 md:tw-pb-3.2">
          <Heading transparent />
        </div>
      </Container>
      <section className="w-full tw-pb-6.4">
        <Container type="dashboard">{renderContent()}</Container>
      </section>
    </main>
  );
};

export default Home;
